<template>
  <v-container id="data-tables-view" fluid tag="section">
    <v-col class="text-right">
      <app-btn v-if="canViewRole" class="mx-2" @click="dialogRoles = true">
        <v-icon>mdi-account-plus-outline</v-icon> Roles
      </app-btn>
      <app-btn v-if="canCreateUser" @click="openCreateDialog">
        <v-icon>mdi-account-plus-outline</v-icon> Add New User
      </app-btn>
    </v-col>
    <material-card icon="mdi-account-group" icon-small color="primary" :title="selectedCompanyName + ' Users'">
      <v-card-text>
        <v-text-field v-model="search" append-icon="mdi-magnify" class="ml-auto" hide-details label="Search records"
          single-line style="max-width: 250px" />

        <v-divider class="mt-3" />

        <v-data-table :headers="headers" :items="users" :search.sync="search" multi-sort :footer-props="{
          showFirstLastPage: true,
          'items-per-page-options': [5, 10, 15],
        }">
          <template v-slot:[`item.role`]="{ item }">
            <div v-if="item.role">
              {{ item.role }}
            </div>
            <div v-else>
              --
            </div>
          </template>
          <template v-slot:top>
            <user-roles-dialog v-if="isSuperUser && rolesUser" :key="rolesUser.id || rolesUser.userId" :user="rolesUser" @close="rolesUser = null" @saved="rolesUser = null; loadUsers()" />
            <roles :show="dialogRoles" :roles="roles" @close="dialogRoles = false" />
            <v-dialog v-model="dialog" max-width="500px">
              <v-card>
                <v-card-title>
                  <span class="headline">{{ formTitle }}</span>
                </v-card-title>

                <v-card-text>
                  <v-container>
                    <v-form ref="UserForm">
                      <v-row>
                        <v-col cols="12">
                          <v-text-field v-model="editedItem.firstName" label="First Name" />
                        </v-col>
                        <v-col cols="12">
                          <v-text-field v-model="editedItem.lastName" label="Last Name" />
                        </v-col>
                        <v-col cols="12">
                          <v-text-field v-model="editedItem.userName" label="Username" :disabled="editedIndex > -1" />
                        </v-col>
                        <v-col cols="12">
                          <v-text-field v-model="editedItem.email" label="Email" />
                        </v-col>
                        <v-col cols="12">
                          <v-text-field v-if="editedIndex < 0" v-model="editedItem.password" label="Password" />
                        </v-col>
                        <v-col v-if="editedIndex < 0" cols="12">
                          <v-select
                            v-model="editedItem.role"
                            :items="availableRoles"
                            label="Role"
                          />
                        </v-col>
                      </v-row>
                    </v-form>
                  </v-container>
                </v-card-text>

                <v-card-actions>
                  <v-spacer />
                  <v-btn color="blue darken-1" text @click="close">
                    Cancel
                  </v-btn>
                  <v-btn color="blue darken-1" text @click="save">
                    Save
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog v-model="dialogDelete" max-width="500px">
              <v-card>
                <v-card-title class="headline">
                  Are you sure you want to delete this item?
                </v-card-title>
                <v-card-actions>
                  <v-spacer />
                  <v-btn color="blue darken-1" text @click="closeDelete">
                    Cancel
                  </v-btn>
                  <v-btn color="blue darken-1" text @click="deleteItemConfirm">
                    OK
                  </v-btn>
                  <v-spacer />
                </v-card-actions>
              </v-card>
            </v-dialog>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn v-if="isSuperUser" small text color="primary" :disabled="!(item.id || item.userId)" @click="rolesUser = item">Manage roles</v-btn>
            <v-icon v-if="canEditUser" medium color="info" class="mr-2" @click="editItem(item)">
              mdi-pencil
            </v-icon>

            <v-icon v-if="canDeleteUser" medium color="error" @click="deleteItem(item)">
              mdi-delete
            </v-icon>
          </template>
        </v-data-table>
      </v-card-text>
    </material-card>
  </v-container>
</template>
<script>
import UserDataService from '@/services/UserDataService'
import Roles from './components/Roles.vue'
import UserRolesDialog from './components/UserRolesDialog.vue'
import { roleNames } from '@/util/userRoles'
import { get } from 'vuex-pathify'

export default {
  name: 'DataTablesView',
  components: { Roles, UserRolesDialog },
  data: () => ({
    rules: {
      required: (value) => !!value || 'Required.',
      length15: (value) => value.length <= 15 || 'Maximum 15 characters.',
    },
    dialog: false,
    dialogDelete: false,
    dialogRoles: false,
    rolesUser: null,
    headers: [],
    users: [],
    roles: [],
    editedIndex: -1,
    editedItem: {
      firstName: '',
      lastName: '',
      userName: '',
      email: '',
      password: '',
      role: 'USER',
    },
    defaultItem: {
      firstName: '',
      lastName: '',
      userName: '',
      email: '',
      password: '',
      role: 'USER',
    },
    search: undefined,
  }),

  computed: {
    availableRoles() {
      return this.roles.map(role => typeof role === 'string' ? role : role.name).filter(Boolean)
    },
    formTitle() {
      return this.editedIndex === -1 ? 'Add New User' : 'Edit User'
    },
    permissions() {
      return this.$store.getters['authUser/permissionsList']
    },
    isSuperUser() {
      return this.$store.getters['authUser/isSuperUser']
    },
    currentCompanyId() {
      return this.$store.getters['authUser/currentCompanyId']
    },
    effectiveCompanyId() {
      return this.selectedCompanyId || this.currentCompanyId || 0
    },
    canCreateUser() {
      return this.isSuperUser
    },
    canEditUser() {
      return this.isSuperUser
    },
    canDeleteUser() {
      return this.hasPermission('Permissions.Users.Delete')
    },
    canViewRole() {
      return this.hasPermission('Permissions.Roles.ViewAll')
    },
    ...get('company', ['selectedCompanyId', 'selectedCompanyName']),
  },

  watch: {
    dialog(val) {
      val || this.close()
    },
    dialogDelete(val) {
      val || this.closeDelete()
    },
    effectiveCompanyId() {
      this.rolesUser = null
      this.loadUsers()
    },
  },

  created() {
    this.initialize()
  },

  methods: {
    initialize() {
      this.loadUsers()
      UserDataService.getAllRoles().then((response) => {
        this.roles = roleNames(response.data).map(name => {
          const original = Array.isArray(response.data) && response.data.find(role => role.name === name)
          return original || { name }
        })
      }).catch((error) => {
        this.$toast.error(this.getApiErrorMessage(error, 'Could not load roles.'), 'Error', {
          position: 'topRight',
        })
      })

      if (this.canEditUser || this.canDeleteUser) {
        this.headers = [
          {
            text: 'Username',
            align: 'start',
            sortable: false,
            value: 'userName',
          },
          { text: 'First Name', value: 'firstName' },
          { text: 'Last Name', value: 'lastName' },
          { text: 'Email', value: 'email' },
          // { text: 'Role', value: 'role' },
          { text: 'Actions', align: 'end', value: 'actions', sortable: false },
        ]
      } else {
        this.headers = [
          {
            text: 'Username',
            align: 'start',
            sortable: false,
            value: 'userName',
          },
          { text: 'First Name', value: 'firstName' },
          { text: 'Last Name', value: 'lastName' },
          { text: 'Email', value: 'email' },
          // { text: 'Role', value: 'role' },
        ]
      }
    },

    hasPermission(permission) {
      return this.permissions.includes(permission)
    },

    normalizeUser(user) {
      return {
        ...user,
        id: user.id || user.userId,
        userId: user.userId || user.id,
        userName: user.userName || user.username || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        role: user.role || 'USER',
        companyId: user.companyId || this.effectiveCompanyId,
      }
    },

    loadUsers() {
      if (!this.effectiveCompanyId) {
        this.users = []
        return
      }

      UserDataService.getAll(this.effectiveCompanyId).then((response) => {
        const users = Array.isArray(response.data?.data) ? response.data.data : []
        this.users = users
          .map(this.normalizeUser)
          .filter(user => Number(user.companyId) === Number(this.effectiveCompanyId))
      }).catch((error) => {
        this.users = []
        this.$toast.error(this.getApiErrorMessage(error, 'Could not load users.'), 'Error', {
          position: 'topRight',
        })
      })
    },

    getApiErrorMessage(error, fallbackMessage) {
      const status = error?.response?.status
      const message = error?.response?.data?.message || error?.response?.data?.error

      if (status === 401 || status === 403) {
        return message || 'This action now requires the SuperUser role.'
      }

      return message || fallbackMessage
    },

    editItem(item) {
      this.editedIndex = this.users.indexOf(item)
      this.editedItem = this.normalizeUser(item)
      this.dialog = true
    },

    openCreateDialog() {
      if (!this.isSuperUser) {
        this.$toast.warning('You are not authorized to add new users.', 'Warning', {
          position: 'topRight',
        })
        return
      }

      this.dialog = true
    },

    deleteItem(item) {
      this.editedIndex = this.users.indexOf(item)
      this.editedItem = this.normalizeUser(item)
      this.dialogDelete = true
    },

    deleteItemConfirm() {
      UserDataService.delete(this.editedItem.id || this.editedItem.userId)
        .then((response) => {
          if (response.status === 200) {
            this.users.splice(this.editedIndex, 1)
            this.closeDelete()
            this.$toast.success('User deleted successfully.', 'Success', {
              position: 'topRight',
            })
          }
        })
        .catch((error) => {
          this.$toast.error(this.getApiErrorMessage(error, 'Could not delete user.'), 'Error', {
            position: 'topRight',
          })
        })
    },

    close() {
      this.dialog = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      })
    },

    closeDelete() {
      this.dialogDelete = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      })
    },

    save() {
      if (this.$refs.UserForm.validate()) {
        if (this.editedIndex > -1) {
          if (!this.isSuperUser) {
            this.$toast.error('Updating users now requires the SuperUser role.', 'Error', {
              position: 'topRight',
            })
            return
          }

          const data = { ...this.editedItem }
          delete data.role
          const index = this.editedIndex
          UserDataService.update(data).then((response) => {
            if (response.status === 200) {
              this.$set(this.users, index, this.normalizeUser(response.data?.data || data))
              this.close()
              this.$toast.success('User updated successfully.', 'Success', {
                position: 'topRight',
              })
            }
          }).catch((error) => {
            this.$toast.error(this.getApiErrorMessage(error, 'Could not update user.'), 'Error', {
              position: 'topRight',
            })
          })
        } else {
          if (!this.isSuperUser) {
            this.$toast.error('Creating users now requires the SuperUser role.', 'Error', {
              position: 'topRight',
            })
            return
          }

          if (this.effectiveCompanyId === 0) {
            this.$toast.error('Company not found.', 'Error', {
              position: 'topRight',
            })
            return
          }

          const payload = {
            username: this.editedItem.userName,
            firstName: this.editedItem.firstName,
            lastName: this.editedItem.lastName,
            email: this.editedItem.email,
            password: this.editedItem.password,
            role: this.editedItem.role,
            companyId: this.effectiveCompanyId,
          }

          const createdUser = {
            userName: payload.username,
            ...payload,
          }

          UserDataService.create(payload).then((response) => {
            if (response.data?.success !== false) {
              this.close()
              this.$toast.success('User added successfully.', 'Success', {
                position: 'topRight',
              })
              this.users.unshift(response.data?.data || createdUser)
            } else {
              this.$toast.error(response.data?.message || 'Could not create user.', 'Error', {
                position: 'topRight',
              })
            }
          }).catch((error) => {
            this.$toast.error(this.getApiErrorMessage(error, 'Could not create user.'), 'Error', {
              position: 'topRight',
            })
          })
        }
      }
    },
  },
}
</script>
